import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { pushToast } from '../lib/toast';
import { toasts as copy } from '@shared/copy';
import { APP_VERSION, APP_BUILT_AT } from '@shared/version';

export function Settings({ user }: { user: { name: string; email: string } }) {
  const qc = useQueryClient();
  const tokens = useQuery({ queryKey: ['mcp-tokens'], queryFn: api.listMcpTokens });
  const [newToken, setNewToken] = useState<{ token: string; name: string } | null>(null);
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => api.createMcpToken(name.trim()),
    onSuccess: (d) => {
      setNewToken({ token: d.token, name: d.name });
      setName('');
      qc.invalidateQueries({ queryKey: ['mcp-tokens'] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteMcpToken(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-tokens'] }),
  });
  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      pushToast(copy.signedOut);
      window.location.href = '/';
    },
  });

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 md:py-8 space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-zinc-500">The boring but important bits.</p>
      </header>

      <section>
        <h2 className="text-sm uppercase tracking-wider text-zinc-500 mb-2">Account</h2>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 text-sm space-y-1">
          <div><span className="text-zinc-500">Name:</span> {user.name}</div>
          <div><span className="text-zinc-500">Email:</span> {user.email}</div>
        </div>
        <button
          onClick={() => logout.mutate()}
          className="mt-3 text-sm rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 hover:bg-zinc-800"
        >
          Sign out
        </button>
      </section>

      <section>
        <h2 className="text-sm uppercase tracking-wider text-zinc-500 mb-2">MCP tokens</h2>
        <p className="text-xs text-zinc-500 mb-3">
          Bearer tokens for the Doer MCP server at <code className="text-amber-400">/mcp</code>. Treat like a password. Shown once.
        </p>
        <ul className="divide-y divide-zinc-800 rounded-xl border border-zinc-800 overflow-hidden">
          {(tokens.data ?? []).map((t: any) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{t.name}</div>
                <div className="text-xs text-zinc-500">{t.id} · created {new Date(t.created_at).toLocaleDateString()}{t.revoked_at ? ' · revoked' : ''}</div>
              </div>
              {!t.revoked_at && (
                <button onClick={() => del.mutate(t.id)} className="p-1.5 text-red-400 hover:bg-zinc-800 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
          {(!tokens.data || tokens.data.length === 0) && (
            <li className="px-3 py-3 text-sm text-zinc-500">None yet.</li>
          )}
        </ul>
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Token name (e.g. Claude Desktop)"
            className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
          />
          <button className="rounded-lg bg-amber-500 text-black px-3 py-2 text-sm font-medium">Create</button>
        </form>

        {newToken && (
          <div className="mt-3 rounded-xl border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
            <div className="font-medium mb-1">Copy this. You won't see it again.</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-zinc-950 rounded px-2 py-1 break-all font-mono text-xs">{newToken.token}</code>
              <button
                className="rounded bg-amber-500 text-black px-2 py-1 text-xs"
                onClick={() => {
                  navigator.clipboard.writeText(newToken.token);
                  pushToast('Token copied. Don\'t lose it.', 'success');
                }}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm uppercase tracking-wider text-zinc-500 mb-2">Keyboard</h2>
        <ul className="text-sm text-zinc-400 space-y-1">
          <li><kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">?</kbd> · Show the full cheat sheet</li>
          <li><kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">n</kbd> · Quick add (Shift+Enter for bulk)</li>
          <li><kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">j</kbd> / <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">k</kbd> · Next / prev row</li>
          <li><kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">Space</kbd> · Toggle complete · <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">s</kbd> · Star · <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">e</kbd> · Edit</li>
          <li><kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">⌘K</kbd> / <kbd className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">Ctrl+K</kbd> · Search</li>
        </ul>
      </section>

      <section className="text-xs text-zinc-600">
        <div>
          Doer <code className="font-mono">{APP_VERSION}</code> · built {new Date(APP_BUILT_AT).toLocaleString()}
        </div>
      </section>
    </div>
  );
}
